import React, { useState, useRef } from 'react';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import Select from '../../BaseComponents/Select';
import Button from '../../BaseComponents/Button';
import { ReactComponent as FileIcon } from '../../../assets/icons/File.svg';
import { ReactComponent as Selected } from '../../../assets/icons/Radiobutton-s.svg';
import { ReactComponent as NotSelected } from '../../../assets/icons/Radiobutton-n.svg';
import { uploadMemberFile } from '../../../api/members';
import './Files.scss';

interface AddFileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (fileData: any) => void;
    memberId?: string;
}

const fileTypes = [
    { key: 'imaging_scans', value: 'Imaging & Scans' },
    { key: 'lab_results', value: 'Lab Results' },
    { key: 'reports_letters', value: 'Reports & Letters' },
    { key: 'medications', value: 'Medications' },
    { key: 'visits_records', value: 'Visits & Records' },
    { key: 'patient_uploads', value: 'Patient Uploads' },
];

const AddFileModal: React.FC<AddFileModalProps> = ({ isOpen, onClose, onUpload, memberId }) => {
    const [fileName, setFileName] = useState('');
    const [fileType, setFileType] = useState<{ key: string; value: string } | null>(null);
    const [isVisibleToMember, setIsVisibleToMember] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (file: File) => {
        if (file) {
            if (file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name)) {
                setError('image cannot be uploaded');
                setSelectedFile(null);
                return;
            }
            setError(null);
            setSelectedFile(file);
            //   if (!fileName) {
            //     setFileName(file.name);
            //   }
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!fileType || !selectedFile || !memberId) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setIsUploading(true);
            setError(null);

            const response = await uploadMemberFile(
                memberId,
                selectedFile,
                fileType.key, // Use key (snake_case) for API
                fileName || undefined,
                !isVisibleToMember
            );

            onUpload({
                fileName: fileName || selectedFile.name,
                name: fileName,
                type: fileType.value, // Use value (display name) for UI
                file: selectedFile,
                url: response?.url || URL.createObjectURL(selectedFile),
                isVisibleToMember,
                ...response
            });
            handleClose();
        } catch (err: any) {
            const serverMessage = err?.response?.data?.description || err?.response?.data?.message || err?.response?.data?.error || err?.message;
            setError(serverMessage || 'Failed to upload file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setFileName('');
        setFileType(null);
        setIsVisibleToMember(false);
        setSelectedFile(null);
        setError(null);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add File"
            width="500px"
        >
            <div className="add-file-modal-container">
                <div className="main-info-container">
                    <div className="section-title">Name of the file </div>
                    <TextInput
                        name="fileName"
                        placeholder="Enter file name"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        isError={false}
                    />
                </div>

                <div className="main-info-container">
                    <div className="section-title">Type *</div>
                    <Select
                        name="fileType"
                        placeholder="Select Type"
                        options={fileTypes}
                        selectedOption={fileType}
                        onChange={(option) => setFileType(option)}
                        isError={false}
                    />
                </div>

                <div className="main-info-container">
                    <div className="section-title">Visible to Member</div>
                    <div className="radio-group">
                        <div
                            className={`radio-option ${isVisibleToMember ? 'active' : ''}`}
                            onClick={() => setIsVisibleToMember(true)}
                        >
                            {isVisibleToMember ? <Selected /> : <NotSelected />}
                            <span>Yes</span>
                        </div>
                        <div
                            className={`radio-option ${!isVisibleToMember ? 'active' : ''}`}
                            onClick={() => setIsVisibleToMember(false)}
                        >
                            {!isVisibleToMember ? <Selected /> : <NotSelected />}
                            <span>No</span>
                        </div>
                    </div>
                </div>

                <div className="main-info-container">
                    <div className="section-title">Upload File *</div>
                    <div
                        className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
                        onDrop={handleDrop}
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <div className="upload-icon">
                            <FileIcon width="48" height="48" />
                        </div>
                        {selectedFile ? (
                            <p className="file-selected">{selectedFile.name}</p>
                        ) : (
                            <>
                                <p className="primary-text">Click to upload file</p>
                                <p className="secondary-text">or drag and drop here</p>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                            style={{ display: 'none' }}
                        />
                    </div>
                </div>

                {error && (
                    <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
                        {error}
                    </div>
                )}

                <div className="btn-wrapper">
                    <Button
                        text="Add File"
                        onClick={handleSubmit}
                        isLoading={isUploading}
                        isDisabled={!fileType || !selectedFile}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default AddFileModal;
